import { useEffect, useRef, useState, type ReactNode } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "../auth/AuthContext";
import { useLocale } from "../../shared/i18n/LocaleContext";
import { AppModal } from "../../shared/components/AppModal";
import { useAppConfirm } from "../../shared/components/AppConfirmDialog";
import { PagedListControls } from "../../shared/components/PagedListControls";
import { PageLoading } from "../../shared/components/PageLoading";
import { listParams, SortableHeaders, useUrlServerSortedRows } from "../../shared/components/SortableTableHeader";
import { IntegrationProtectionBoundary } from "../../shared/integrations/IntegrationManagedField";
import { useIntegrationProtection } from "../../shared/integrations/useIntegrationProtection";
import { getPreferredPageSize, rememberPageSize } from "../../shared/pagination/pageSizePreference";
import {
  LocalizedClockInput,
  LocalizedDateInput,
  LocalizedDecimalInput,
} from "../../shared/i18n/LocalizedInputs";
import { getMaintenance, getMaintenanceDeletionAvailability, maintenanceCommand } from "./maintenanceApi";
import type { Asset, MaintenanceSnapshot, Plan, WorkOrder } from "./types";
export type MaintenanceSection = "overview" | "assets" | "plans" | "orders";
const sections = [
  { key: "assets", route: "/app/mantenimiento/activos" },
  { key: "plans", route: "/app/mantenimiento/planes" },
  { key: "orders", route: "/app/mantenimiento/ordenes" },
] as const;
export function MaintenancePage({
  section = "overview",
}: {
  section?: MaintenanceSection;
}) {
  const { user } = useAuth();
  const { t, formatNumber, formatDate, formatDateTime } = useLocale();
  const { confirm, confirmationDialog } = useAppConfirm();
  const canEdit = user?.permissions.maintenance === "Write";
  const entityBySection = { assets: "MaintenanceAsset", plans: "MaintenancePlan", orders: "MaintenanceWorkOrder" } as const;
  const activeProtection = useIntegrationProtection(entityBySection[section === "overview" ? "assets" : section]);
  const canPlan = user?.permissions.planning === "Write";
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const page = Math.max(1, Number(searchParams.get("page") ?? 1) || 1);
  const pageSize = getPreferredPageSize(searchParams);
  const appliedQuery = searchParams.get("q") ?? "";
  const appliedStatus = searchParams.get("status") ?? "";
  const appliedSort = searchParams.get("sort") ?? "code";
  const appliedDirection = searchParams.get("direction") === "desc" ? "desc" : "asc";
  const [query, setQuery] = useState(appliedQuery);
  const [status, setStatus] = useState(appliedStatus);
  useEffect(() => { setQuery(appliedQuery); setStatus(appliedStatus); },
    [appliedQuery, appliedStatus]);
  const [data, setData] = useState<MaintenanceSnapshot | null>(null);
  const [modal, setModal] = useState<Exclude<
    MaintenanceSection,
    "overview"
  > | null>(null);
  const [editing, setEditing] = useState<Asset | Plan | WorkOrder | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const loadSequence = useRef(0);
  const load = async () => {
    const sequence = ++loadSequence.current;
    try {
      const result = await getMaintenance(section === "overview" ? undefined : {
        section, query:appliedQuery, status:appliedStatus, page, pageSize,
        sort:appliedSort, direction:appliedDirection});
      if (sequence !== loadSequence.current) return;
      setData(result);
      setError(null);
    } catch (e) {
      if (sequence === loadSequence.current)
        setError(e instanceof Error ? e.message : "maintenance.loadError");
    }
  };
  useEffect(() => {
    void load();
  }, [user?.activeCompanyId, section, appliedQuery, appliedStatus, page, pageSize,
    appliedSort, appliedDirection]);
  useEffect(() => {
    const id = searchParams.get("edit");
    if (!data) return;
    setError(null);
    if (section === "overview") { setEditing(null); setModal(null); return; }
    if (searchParams.get("new") === "1" && canEdit && activeProtection.canCreate) { setEditing(null); setModal(section); return; }
    if (!id) { setEditing(null); setModal(null); return; }
    const item = data[section].find((candidate) => candidate.id === id);
    if (item) {
      setEditing(item);
      setModal(section);
    } else { setEditing(null); setModal(null); }
  }, [activeProtection.canCreate, canEdit, data, searchParams, section]);
  const closeEditor = () => {
    setError(null); setModal(null); setEditing(null);
    const next = new URLSearchParams(searchParams); next.delete("edit"); next.delete("new");
    setSearchParams(next, { replace: true });
  };
  const save = async (path: string, body: unknown) => {
    try {
      const result = await maintenanceCommand(
        path, editing ? "PUT" : "POST", body,
      );
      setMessage(result.code);
      closeEditor();
      if (result.planningScenarioRequired && canPlan) {
        if (await confirm(t("planning.changeRequiresSimulation"))) {
          navigate("/app/planificacion?scenario=global");
          return;
        }
      }
      await load();
    } catch (e) {
      setError(e instanceof Error ? e.message : "maintenance.actionError");
    }
  };
  const remove = async () => {
    if (!editing || !modal) return;
    const route = modal === "assets" ? "assets" : modal === "plans" ? "plans" : "orders";
    const path = `/${route}/${editing.id}`;
    setError(null);
    try { const availability=await getMaintenanceDeletionAvailability(path);
      if(!availability.allowed){setError(availability.code);return;}
      if(!await confirm(t("deletion.confirm"), undefined, true))return;
      const result = await maintenanceCommand(path, "DELETE");
      setMessage(result.code); closeEditor(); await load();
      if(result.planningScenarioRequired&&canPlan&&
        await confirm(t("planning.changeRequiresSimulation")))
        navigate("/app/planificacion?scenario=global");
    } catch (reason) { setError(reason instanceof Error ? reason.message : "deletion.unavailable"); }
  };
  const listRows:Array<Asset|Plan|WorkOrder>=data&&section!=="overview"?[...data[section]]:[];
  const tableSort=useUrlServerSortedRows(listRows,{code:item=>"number"in item?item.number:item.code,
    name:item=>"title"in item?item.title:item.name,asset:item=>"assetId"in item?data?.assets.find(x=>x.id===item.assetId)?.name:null,
    resource:item=>isAsset(item)?data?.resources.find(x=>x.id===item.resourceId)?.name:null,
    criticality:item=>isAsset(item)?item.criticality:null,frequency:item=>isPlan(item)?item.frequencyDays:null,
    nextDue:item=>isPlan(item)?item.nextDueOn:null,priority:item=>isOrder(item)?item.priority:null,
    status:item=>isOrder(item)?item.status:"isActive"in item?item.isActive:null,due:item=>isOrder(item)?item.dueAtUtc:null},"code","asc");
  if (!data) return <PageLoading label={t("maintenance.loading")}
    error={error ? t(error as Parameters<typeof t>[0]) : null}/>;
  const assetName = (id: string) =>
    data.assets.find((x) => x.id === id)?.name ?? "—";
  return (
    <main className="page-content">
      <header className="welcome administration-heading">
        <div>
          <span className="eyebrow">{t("maintenance.eyebrow")}</span>
          <h1>
            {t(
              section === "overview"
                ? "maintenance.title"
                : `maintenance.${section}`,
            )}
          </h1>
          <p>
            {data.companyName} · {t("maintenance.subtitle")}
          </p>
        </div>
      </header>
      {error && !modal && (
        <p className="form-error">{t(error as Parameters<typeof t>[0])}</p>
      )}
      {message && (
        <p className="form-success">{t(message as Parameters<typeof t>[0])}</p>
      )}
      {section === "overview" ? (
        <>
          <section className="maintenance-kpis">
            <Kpi
              label={t("maintenance.openOrders")}
              value={
                data.overview.openOrders
              }
            />
            <Kpi
              label={t("maintenance.urgentOrders")}
              value={
                data.overview.urgentOrders
              }
            />
            <Kpi
              label={t("maintenance.overduePlans")}
              value={
                data.overview.overduePlans
              }
            />
            <Kpi
              label={t("maintenance.downtime")}
              value={data.overview.downtimeHours}
              suffix=" h"
            />
          </section>
          <section className="capacity-overview-grid">
            {sections.map((x) => (
              <Link className="capacity-overview-card" to={x.route} key={x.key}>
                <span className="capacity-overview-count">
                  {formatNumber(data.overview[x.key])}
                </span>
                <h2>{t(`maintenance.${x.key}`)}</h2>
                <p>{t(`maintenance.${x.key}Description`)}</p>
                <span className="capacity-overview-link">
                  {t("capacity.openSection")} →
                </span>
              </Link>
            ))}
          </section>
        </>
      ) : (
        <section className="data-panel administration-panel">
          <div className="section-heading">
            <h2>{t(`maintenance.${section}`)}</h2>
            {canEdit && activeProtection.canCreate && (
              <button className="primary-button" onClick={() => setSearchParams({ ...Object.fromEntries(searchParams), new: "1" })}>
                + {t("administration.add")}
              </button>
            )}
          </div>
          <PagedListControls applyLabel={t("recordList.applyFilters")} clearLabel={t("recordList.clearFilters")}
            rowsPerPageLabel={t("recordList.rowsPerPage")} previousLabel={t("recordList.previousPage")}
            nextLabel={t("recordList.nextPage")} pageLabel={t("recordList.pageOf")}
            totalLabel={t("recordList.totalRecords")} page={page} pageSize={pageSize}
            totalCount={data.totalCount} formatNumber={formatNumber}
            filters={<><label><span>{t("nav.search")}</span><input value={query}
              placeholder={t("recordList.search")} onChange={event=>setQuery(event.target.value)}/></label>
              <label><span>{t("administration.status")}</span><select value={status} onChange={event=>setStatus(event.target.value)}>
                <option value="">{t("recordList.allStatuses")}</option>
                {section === "orders" ? ["Requested","Planned","InProgress","Paused","Completed","Cancelled"].map(value=><option value={value} key={value}>{t(`maintenance.status.${value}`)}</option>) : <>
                  <option value="active">{t("administration.active")}</option><option value="inactive">{t("administration.inactive")}</option></>}
              </select></label></>}
            onApply={()=>setSearchParams(listParams(searchParams,{q:query.trim()||undefined,status:status||undefined,page:"1",pageSize:String(pageSize)}))}
            onClear={()=>{setQuery("");setStatus("");setSearchParams(listParams(searchParams,{page:"1",pageSize:String(pageSize)}))}}
            onPageChange={next=>setSearchParams(listParams(searchParams,{q:appliedQuery||undefined,status:appliedStatus||undefined,page:String(next),pageSize:String(pageSize)}))}
            onPageSizeChange={next=>{rememberPageSize(next);setSearchParams(listParams(searchParams,{q:appliedQuery||undefined,status:appliedStatus||undefined,page:"1",pageSize:String(next)}))}}/>
          <div className="administration-table-wrap">
            <table className="data-table administration-table">
              <thead>
                <tr>
                  <SortableHeaders sort={tableSort} columns={[["code",t(section === "orders"?"maintenance.number":"aplPanel.code")],["name",t("aplPanel.name")],...(section!=="assets"?[["asset",t("maintenance.asset")] as const]:[]),...(section==="assets"?[["resource",t("maintenance.resource")],["criticality",t("maintenance.criticality")]] as const:[]),...(section==="plans"?[["frequency",t("maintenance.frequency")],["nextDue",t("maintenance.nextDue")]] as const:[]),...(section==="orders"?[["priority",t("maintenance.priority")],["status",t("planning.status")],["due",t("maintenance.due")]] as const:[])]}/>
                  {/* Columnas declaradas arriba para conservar el mismo orden en móvil. */}
                </tr>
              </thead>
              <tbody>
                {tableSort.sortedRows.map((item) => (
                  <tr key={item.id}>
                    <td>
                      <Link className="record-code-link" to={`?${new URLSearchParams({ ...Object.fromEntries(searchParams), edit: item.id }).toString()}`}
                        state={{recordIds:tableSort.sortedRows.map(value=>value.id)}}>
                        {"number" in item ? item.number : item.code}
                      </Link>
                    </td>
                    <td>{"title" in item ? item.title : item.name}</td>
                    {section !== "assets" && (
                      <td>{assetName((item as Plan | WorkOrder).assetId)}</td>
                    )}
                    {section === "assets" && isAsset(item) && (
                      <>
                        <td>
                          {data.resources.find((x) => x.id === item.resourceId)
                            ?.name ?? "—"}
                        </td>
                        <td>
                          {t(`maintenance.criticality.${item.criticality}`)}
                        </td>
                      </>
                    )}
                    {section === "plans" && isPlan(item) && (
                      <>
                        <td>
                          {formatNumber(item.frequencyDays)}{" "}
                          {t("maintenance.days")}
                        </td>
                        <td>{formatDate(item.nextDueOn)}</td>
                      </>
                    )}
                    {section === "orders" && isOrder(item) && (
                      <>
                        <td>{t(`maintenance.priority.${item.priority}`)}</td>
                        <td>{t(`maintenance.status.${item.status}`)}</td>
                        <td>
                          {item.dueAtUtc ? formatDateTime(item.dueAtUtc) : "—"}
                        </td>
                      </>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {data[section].length === 0 && (
            <p className="empty-state">{t("administration.empty")}</p>
          )}
        </section>
      )}
      {modal && (
        <AppModal
          title={t(editing ? "maintenance.edit" : "maintenance.create")}
          closeLabel={t("nav.closeMenu")}
          onClose={closeEditor}
          wide
          saveForm={canEdit}
          onDelete={editing && canEdit && activeProtection.canDelete ? remove : null}
          feedback={error ? t(error as Parameters<typeof t>[0]) : null}
        >
          <fieldset className={!canEdit ? "readonly-detail" : undefined} disabled={!canEdit}>{modal === "assets" ? (
            <AssetForm key={editing?.id ?? "new"}
              item={isAsset(editing) ? editing : null}
              data={data}
              save={(b) => save(`/assets${editing ? `/${editing.id}` : ""}`, b)}
            />
          ) : modal === "plans" ? (
            <PlanForm key={editing?.id ?? "new"}
              item={isPlan(editing) ? editing : null}
              data={data}
              save={(b) => save(`/plans${editing ? `/${editing.id}` : ""}`, b)}
            />
          ) : (
            <OrderForm key={editing?.id ?? "new"}
              item={isOrder(editing) ? editing : null}
              data={data}
              save={(b) => save(`/orders${editing ? `/${editing.id}` : ""}`, b)}
            />
          )}</fieldset>
        </AppModal>
      )}
      {confirmationDialog}
    </main>
  );
}
function Kpi({
  label,
  value,
  suffix = "",
}: {
  label: string;
  value: number;
  suffix?: string;
}) {
  return (
    <article className="indicator-card neutral maintenance-kpi">
      <span className="indicator-label">{label}</span>
      <div className="indicator-value">
        {value}
        {suffix}
      </div>
    </article>
  );
}
function isAsset(x: unknown): x is Asset {
  return !!x && typeof x === "object" && "criticality" in x;
}
function isPlan(x: unknown): x is Plan {
  return !!x && typeof x === "object" && "frequencyDays" in x;
}
function isOrder(x: unknown): x is WorkOrder {
  return !!x && typeof x === "object" && "priority" in x;
}
function Field({ label, children }: { label: string; children: ReactNode }) {
  return (
    <label>
      <span>{label}</span>
      {children}
    </label>
  );
}
function Actions() {
  const { t } = useLocale();
  return (
    <div className="form-actions">
      <button className="primary-button">{t("administration.save")}</button>
    </div>
  );
}
function AssetForm({
  item,
  data,
  save,
}: {
  item: Asset | null;
  data: MaintenanceSnapshot;
  save: (x: unknown) => void;
}) {
  const { t, locale } = useLocale();
  const [f, setF] = useState({
    code: item?.code ?? "",
    name: item?.name ?? "",
    description: item?.description ?? "",
    resourceId: item?.resourceId ?? "",
    parentAssetId: item?.parentAssetId ?? "",
    manufacturer: item?.manufacturer ?? "",
    model: item?.model ?? "",
    serialNumber: item?.serialNumber ?? "",
    location: item?.location ?? "",
    criticality: item?.criticality ?? "Medium",
    commissionedOn: item?.commissionedOn ?? "",
    isActive: item?.isActive ?? true,
  });
  return (
    <IntegrationProtectionBoundary entity="MaintenanceAsset" fields={['code','name','resourceCode','parentAssetCode','criticality','commissionedOn','manufacturer','model','serialNumber','location','description','isActive']}><form
      className="administration-form modal-form"
      onSubmit={(e) => {
        e.preventDefault();
        save({
          ...f,
          code: item ? null : f.code,
          resourceId: f.resourceId || null,
          parentAssetId: f.parentAssetId || null,
          commissionedOn: f.commissionedOn || null,
        });
      }}
    >
      <div className="compact-form-grid">
        <Field label={t("aplPanel.code")}>
          <input
            required
            disabled={!!item}
            value={f.code}
            onChange={(e) => setF({ ...f, code: e.target.value })}
          />
        </Field>
        <Field label={t("aplPanel.name")}>
          <input
            required
            value={f.name}
            onChange={(e) => setF({ ...f, name: e.target.value })}
          />
        </Field>
        <Field label={t("maintenance.resource")}>
          <select
            value={f.resourceId}
            onChange={(e) => setF({ ...f, resourceId: e.target.value })}
          >
            <option value="">—</option>
            {data.resources.map((x) => (
              <option key={x.id} value={x.id}>
                {x.code} · {x.name}
              </option>
            ))}
          </select>
        </Field>
        <Field label={t("maintenance.parentAsset")}>
          <select
            value={f.parentAssetId}
            onChange={(e) => setF({ ...f, parentAssetId: e.target.value })}
          >
            <option value="">—</option>
            {data.assets
              .filter((x) => x.id !== item?.id)
              .map((x) => (
                <option key={x.id} value={x.id}>
                  {x.code} · {x.name}
                </option>
              ))}
          </select>
        </Field>
        <Field label={t("maintenance.criticality")}>
          <select
            value={f.criticality}
            onChange={(e) =>
              setF({
                ...f,
                criticality: e.target.value as typeof f.criticality,
              })
            }
          >
            {["Low", "Medium", "High", "Critical"].map((x) => (
              <option key={x} value={x}>
                {t(`maintenance.criticality.${x}`)}
              </option>
            ))}
          </select>
        </Field>
        <Field label={t("maintenance.commissionedOn")}>
          <LocalizedDateInput
            locale={locale}
            value={f.commissionedOn}
            onChange={(commissionedOn) => setF({ ...f, commissionedOn })}
          />
        </Field>
        <Field label={t("maintenance.manufacturer")}>
          <input
            value={f.manufacturer}
            onChange={(e) => setF({ ...f, manufacturer: e.target.value })}
          />
        </Field>
        <Field label={t("maintenance.model")}>
          <input
            value={f.model}
            onChange={(e) => setF({ ...f, model: e.target.value })}
          />
        </Field>
        <Field label={t("maintenance.serial")}>
          <input
            value={f.serialNumber}
            onChange={(e) => setF({ ...f, serialNumber: e.target.value })}
          />
        </Field>
        <Field label={t("maintenance.location")}>
          <input
            value={f.location}
            onChange={(e) => setF({ ...f, location: e.target.value })}
          />
        </Field>
        <Field label={t("materials.description")}>
          <textarea
            value={f.description}
            onChange={(e) => setF({ ...f, description: e.target.value })}
          />
        </Field>
      </div>
      <label className="checkbox-row">
        <input
          type="checkbox"
          checked={f.isActive}
          onChange={(e) => setF({ ...f, isActive: e.target.checked })}
        />
        {t("administration.active")}
      </label>
      <Actions />
    </form></IntegrationProtectionBoundary>
  );
}
function PlanForm({
  item,
  data,
  save,
}: {
  item: Plan | null;
  data: MaintenanceSnapshot;
  save: (x: unknown) => void;
}) {
  const { t, locale } = useLocale();
  const [f, setF] = useState({
    code: item?.code ?? "",
    assetId: item?.assetId ?? "",
    name: item?.name ?? "",
    instructions: item?.instructions ?? "",
    frequencyDays: item?.frequencyDays ?? 30,
    estimatedHours: item?.estimatedHours ?? 1,
    nextDueOn: item?.nextDueOn ?? "",
    stopsResource: item?.stopsResource ?? true,
    isActive: item?.isActive ?? true,
  });
  return (
    <IntegrationProtectionBoundary entity="MaintenancePlan" fields={['code','name','assetCode','frequencyDays','estimatedHours','nextDueOn','instructions','stopsResource','isActive']}><form
      className="administration-form modal-form"
      onSubmit={(e) => {
        e.preventDefault();
        save({ ...f, code: item ? null : f.code });
      }}
    >
      <div className="compact-form-grid">
        <Field label={t("aplPanel.code")}>
          <input
            required
            disabled={!!item}
            value={f.code}
            onChange={(e) => setF({ ...f, code: e.target.value })}
          />
        </Field>
        <Field label={t("aplPanel.name")}>
          <input
            required
            value={f.name}
            onChange={(e) => setF({ ...f, name: e.target.value })}
          />
        </Field>
        <Field label={t("maintenance.asset")}>
          <select
            required
            value={f.assetId}
            onChange={(e) => setF({ ...f, assetId: e.target.value })}
          >
            {<option value="">—</option>}
            {data.assets.map((x) => (
              <option key={x.id} value={x.id}>
                {x.code} · {x.name}
              </option>
            ))}
          </select>
        </Field>
        <Field label={t("maintenance.frequency")}>
          <input
            type="number"
            min="1"
            required
            value={f.frequencyDays}
            onChange={(e) =>
              setF({ ...f, frequencyDays: Number(e.target.value) })
            }
          />
        </Field>
        <Field label={t("maintenance.estimatedHours")}>
          <LocalizedDecimalInput
            locale={locale}
            min={0}
            value={f.estimatedHours}
            onChange={(estimatedHours) => setF({ ...f, estimatedHours })}
          />
        </Field>
        <Field label={t("maintenance.nextDue")}>
          <LocalizedDateInput
            locale={locale}
            required
            value={f.nextDueOn}
            onChange={(nextDueOn) => setF({ ...f, nextDueOn })}
          />
        </Field>
        <Field label={t("maintenance.instructions")}>
          <textarea
            value={f.instructions}
            onChange={(e) => setF({ ...f, instructions: e.target.value })}
          />
        </Field>
      </div>
      <label className="checkbox-row">
        <input
          type="checkbox"
          checked={f.stopsResource}
          onChange={(e) => setF({ ...f, stopsResource: e.target.checked })}
        />
        {t("maintenance.stopsResource")}
      </label>
      <label className="checkbox-row">
        <input
          type="checkbox"
          checked={f.isActive}
          onChange={(e) => setF({ ...f, isActive: e.target.checked })}
        />
        {t("administration.active")}
      </label>
      <Actions />
    </form></IntegrationProtectionBoundary>
  );
}
function OrderForm({
  item,
  data,
  save,
}: {
  item: WorkOrder | null;
  data: MaintenanceSnapshot;
  save: (x: unknown) => void;
}) {
  const { t, locale } = useLocale();
  const date = (x: string | null) =>
    x ? new Date(x).toLocaleDateString("en-CA") : "";
  const clock = (x: string | null) =>
    x ? new Date(x).toTimeString().slice(0, 5) : "";
  const initialStatus =
    item?.status === "InProgress" || item?.status === "Paused"
      ? "Planned"
      : (item?.status ?? "Requested");
  const [f, setF] = useState({
    number: item?.number ?? "",
    assetId: item?.assetId ?? "",
    planId: item?.planId ?? "",
    title: item?.title ?? "",
    description: item?.description ?? "",
    type: item?.type ?? "Corrective",
    priority: item?.priority ?? "Normal",
    status: initialStatus,
    dueAtUtc: date(item?.dueAtUtc ?? null),
    plannedStartAtUtc: date(item?.plannedStartAtUtc ?? null),
    plannedEndAtUtc: date(item?.plannedEndAtUtc ?? null),
    actualStartAtUtc: date(item?.actualStartAtUtc ?? null),
    actualEndAtUtc: date(item?.actualEndAtUtc ?? null),
    dueTime: clock(item?.dueAtUtc ?? null),
    plannedStartTime: clock(item?.plannedStartAtUtc ?? null),
    plannedEndTime: clock(item?.plannedEndAtUtc ?? null),
    actualStartTime: clock(item?.actualStartAtUtc ?? null),
    actualEndTime: clock(item?.actualEndAtUtc ?? null),
    downtimeHours: item?.downtimeHours ?? 0,
    stopsResource: item?.stopsResource ?? true,
    failureMode: item?.failureMode ?? "",
    cause: item?.cause ?? "",
    resolution: item?.resolution ?? "",
  });
  const utc = (x: string, time: string) =>
    x ? new Date(`${x}T${time || "00:00"}:00`).toISOString() : null;
  return (
    <IntegrationProtectionBoundary entity="MaintenanceWorkOrder" fields={['number','title','assetCode','planCode','type','priority','status','dueAtUtc','dueAtUtc','plannedStartAtUtc','plannedStartAtUtc','plannedEndAtUtc','plannedEndAtUtc','actualStartAtUtc','actualStartAtUtc','actualEndAtUtc','actualEndAtUtc','downtimeHours','failureMode','cause','resolution','description','stopsResource']}><form
      className="administration-form modal-form"
      onSubmit={(e) => {
        e.preventDefault();
        save({
          ...f,
          replanAutomatically: false,
          number: item ? null : f.number,
          planId: f.planId || null,
          dueAtUtc: utc(f.dueAtUtc, f.dueTime),
          plannedStartAtUtc: utc(f.plannedStartAtUtc, f.plannedStartTime),
          plannedEndAtUtc: utc(f.plannedEndAtUtc, f.plannedEndTime),
          actualStartAtUtc: utc(f.actualStartAtUtc, f.actualStartTime),
          actualEndAtUtc: utc(f.actualEndAtUtc, f.actualEndTime),
        });
      }}
    >
      <div className="compact-form-grid">
        <Field label={t("maintenance.number")}>
          <input
            required
            disabled={!!item}
            value={f.number}
            onChange={(e) => setF({ ...f, number: e.target.value })}
          />
        </Field>
        <Field label={t("aplPanel.name")}>
          <input
            required
            value={f.title}
            onChange={(e) => setF({ ...f, title: e.target.value })}
          />
        </Field>
        <Field label={t("maintenance.asset")}>
          <select
            required
            value={f.assetId}
            onChange={(e) =>
              setF({ ...f, assetId: e.target.value, planId: "" })
            }
          >
            <option value="">—</option>
            {data.assets.map((x) => (
              <option key={x.id} value={x.id}>
                {x.code} · {x.name}
              </option>
            ))}
          </select>
        </Field>
        <Field label={t("maintenance.plan")}>
          <select
            value={f.planId}
            onChange={(e) => setF({ ...f, planId: e.target.value })}
          >
            <option value="">—</option>
            {data.plans
              .filter((x) => x.assetId === f.assetId)
              .map((x) => (
                <option key={x.id} value={x.id}>
                  {x.code} · {x.name}
                </option>
              ))}
          </select>
        </Field>
        <Field label={t("maintenance.type")}>
          <select
            value={f.type}
            onChange={(e) =>
              setF({ ...f, type: e.target.value as typeof f.type })
            }
          >
            {["Corrective", "Preventive", "Predictive", "Inspection"].map(
              (x) => (
                <option key={x} value={x}>
                  {t(`maintenance.type.${x}`)}
                </option>
              ),
            )}
          </select>
        </Field>
        <Field label={t("maintenance.priority")}>
          <select
            value={f.priority}
            onChange={(e) =>
              setF({ ...f, priority: e.target.value as typeof f.priority })
            }
          >
            {["Low", "Normal", "High", "Urgent"].map((x) => (
              <option key={x} value={x}>
                {t(`maintenance.priority.${x}`)}
              </option>
            ))}
          </select>
        </Field>
        <Field label={t("planning.status")}>
          <select
            value={f.status}
            onChange={(e) =>
              setF({ ...f, status: e.target.value as typeof f.status })
            }
          >
            {["Requested", "Planned", "Completed", "Cancelled"].map((x) => (
              <option key={x} value={x}>
                {t(`maintenance.status.${x}`)}
              </option>
            ))}
          </select>
        </Field>
        {(
          [
            { date: "dueAtUtc", time: "dueTime" },
            { date: "plannedStartAtUtc", time: "plannedStartTime" },
            { date: "plannedEndAtUtc", time: "plannedEndTime" },
            { date: "actualStartAtUtc", time: "actualStartTime" },
            { date: "actualEndAtUtc", time: "actualEndTime" },
          ] as const
        ).map((k) => (
          <Field key={k.date} label={t(`maintenance.${k.date}`)}>
            <div className="maintenance-datetime">
              <LocalizedDateInput
                locale={locale}
                value={f[k.date]}
                onChange={(v) => setF({ ...f, [k.date]: v })}
              />
              <LocalizedClockInput
                locale={locale}
                value={f[k.time]}
                ariaLabel={t("maintenance.time")}
                pickerLabel={t("input.selectTime")}
                hourLabel={t("input.hour")}
                minuteLabel={t("input.minute")}
                dayPeriodLabel={t("input.dayPeriod")}
                onChange={(value) => setF({ ...f, [k.time]: value })}
              />
            </div>
          </Field>
        ))}
        <Field label={t("maintenance.downtime")}>
          <LocalizedDecimalInput
            locale={locale}
            min={0}
            value={f.downtimeHours}
            onChange={(downtimeHours) => setF({ ...f, downtimeHours })}
          />
        </Field>
        <label className="checkbox-row">
          <input
            type="checkbox"
            checked={f.stopsResource}
            onChange={(e) => setF({ ...f, stopsResource: e.target.checked })}
          />
          {t("maintenance.stopsResource")}
        </label>
        <Field label={t("materials.description")}>
          <textarea
            value={f.description}
            onChange={(e) => setF({ ...f, description: e.target.value })}
          />
        </Field>
        <Field label={t("maintenance.failureMode")}>
          <textarea
            value={f.failureMode}
            onChange={(e) => setF({ ...f, failureMode: e.target.value })}
          />
        </Field>
        <Field label={t("maintenance.cause")}>
          <textarea
            value={f.cause}
            onChange={(e) => setF({ ...f, cause: e.target.value })}
          />
        </Field>
        <Field label={t("maintenance.resolution")}>
          <textarea
            value={f.resolution}
            onChange={(e) => setF({ ...f, resolution: e.target.value })}
          />
        </Field>
      </div>
      <Actions />
    </form></IntegrationProtectionBoundary>
  );
}
